import { Component, computed, inject, OnInit, Signal, signal, viewChild } from '@angular/core';
import { AgGridAngular } from 'ag-grid-angular';
import { ColDef, GridOptions, RowSelectedEvent } from 'ag-grid-community';
import { pull, sortBy } from 'lodash';
import { DateTime } from 'luxon';
import { linkedQueryParam } from 'ngxtension/linked-query-param';
import { finalize } from 'rxjs/operators';
import { PanModalComponent } from '../../../components/pan-modal/pan-modal.component';
import { convertDateToTimezone } from '../../../date.utils';
import { tryGetErrorMessage } from '../../../error.utils';
import { caseMeetsMinimumIncidents, defaultTableDefOptions } from '../../../helpers';
import { CaseTrackingService } from '../../../service/case-tracking.service';
import { LocationService } from '../../../service/location.service';
import { NotifyService } from '../../../service/notify.service';
import {
  CaseIncidentTableEntry,
  CaseStatus,
  CaseTrackingLocation,
  CaseWithIncidents,
  LocationResponse,
  Pagination,
  ShareCasesRequest,
} from '../../../types';
import { CellTheftReportVideoComponent } from '../../pages/theft-report/cell-theft-report-video/cell-theft-report-video.component';
import { CellCaseTrackingPhotoComponent } from '../case-tracking-portal/cell-case-tracking-photo/cell-case-tracking-photo.component';
import { CellCaseTrackingGuestActionsComponent } from './cell-case-tracking-guest-actions/cell-case-tracking-guest-actions.component';
import { CellCaseTrackingGuestGroupComponent } from './cell-case-tracking-guest-group/cell-case-tracking-guest-group.component';

type LocationResponseWithGroup = LocationResponse & { _group?: string };

@Component({
  selector: 'app-pan-case-tracking-guest-portal',
  templateUrl: './case-tracking-guest-portal.component.html',
  styleUrl: './case-tracking-guest-portal.component.scss',
  standalone: false,
})
export class CaseTrackingGuestPortalComponent implements OnInit {
  private notifyService = inject(NotifyService);
  private locationService = inject(LocationService);
  private caseTrackingService = inject(CaseTrackingService);

  readonly pageLocationIds = linkedQueryParam<number[]>('location_ids', {
    parse: (value) => (value ? value.split(',').map((v) => parseInt(v, 10)) : []),
    stringify: (value) => value.join(','),
  });

  public caseGrid = viewChild<AgGridAngular>('caseGrid');
  public findingsModal = viewChild<PanModalComponent>('findingsModal');
  public findings = signal<string>('');

  public loadingLocations = signal<boolean>(false);
  public loadingCases = signal<boolean>(false);
  public error = signal<string | undefined>(undefined);

  public selectedLocationIds = signal<number[]>([]);
  public isSharing = signal<boolean>(false);

  public branchesAndLocationsMap = signal<Map<number, LocationResponseWithGroup[]>>(new Map<number, LocationResponseWithGroup[]>());
  public allLocations = signal<LocationResponseWithGroup[]>([]);

  public incidentDateFrom = signal<Date | undefined>(undefined);
  public incidentDateTo = signal<Date | undefined>(undefined);

  public cases = signal<CaseWithIncidents[]>([]);
  public minIncidents = signal<number>(0);
  public selectedCases = signal<CaseIncidentTableEntry[]>([]);

  public sortedLocations = computed(() => sortBy(this.allLocations(), [(loc) => -loc.recent_incidents_count, (loc) => loc.name]));
  public canRefresh = computed(() => this.selectedLocationIds().length > 0);
  public disableActions = computed(() => this.loadingLocations() || this.loadingCases());

  public numNewCases = computed(() =>
    this.cases()
      .filter((f) => f.status === CaseStatus.New && caseMeetsMinimumIncidents(f, this.minIncidents()))
      .length.toString(),
  );
  public numViewedCases = computed(() =>
    this.cases()
      .filter((f) => f.status === CaseStatus.Viewed && caseMeetsMinimumIncidents(f, this.minIncidents()))
      .length.toString(),
  );

  public ungroupedCases: Signal<CaseIncidentTableEntry[]> = computed(() => {
    const minIncidents = this.minIncidents();
    const allCases = this.cases();

    return allCases.flatMap((c) => {
      if (!caseMeetsMinimumIncidents(c, minIncidents)) return [];

      return c.incidents.flatMap((i) => ({
        ...i,
        ...c,
        case_id: c.id,
        case_num: c.case_index,
        instance_num: i.instance_num,
        case_index: `${c.case_index} @ ${c.location_name}`,
        first_incident_date: new Date(c.first_incident_date),
        most_recent_incident_date: new Date(c.most_recent_incident_date),
        incident_date: convertDateToTimezone(i.incident_date, c.location_timezone),
        date_shared: new Date(i.date_shared),
      }));
    });
  });

  public canShareCases = computed(() => Object.keys(this.selectedCases()).length > 0);
  public showShareCase = computed<boolean>(
    () => this.selectedLocationIds() && this.selectedLocationIds().length > 0 && this.cases().length > 0,
  );

  public readonly columnDefs: ColDef[] = [
    {
      field: 'case_index',
      headerName: 'Case #',
      rowGroup: true,
      hide: true,
    },
    {
      field: 'branch_name',
      headerName: 'Branch',
      cellDataType: 'text',
      filter: 'agTextColumnFilter',
      sortable: true,
    },
    {
      field: 'location_name',
      headerName: 'Location',
      cellDataType: 'text',
      filter: 'agTextColumnFilter',
      sortable: true,
    },
    {
      field: 'incident_date',
      headerName: 'Incident Date',
      cellDataType: 'date',
      width: 100,
      sortable: true,
    },
    {
      field: 'date_shared',
      headerName: 'Date Shared',
      cellDataType: 'date',
      width: 100,
      sortable: true,
    },
    {
      field: 'photo',
      headerName: 'Photo',
      suppressColumnsToolPanel: true,
      sortable: false,
      width: 64,
      cellRenderer: CellCaseTrackingPhotoComponent,
    },
    {
      headerName: 'Video',
      suppressColumnsToolPanel: true,
      sortable: false,
      width: 64,
      cellRenderer: CellTheftReportVideoComponent,
      cellRendererParams: { accessType: 'guest_dashboard' },
    },
    {
      field: 'actions',
      headerName: '',
      suppressColumnsToolPanel: true,
      suppressKeyboardEvent: () => true,
      sortable: false,
      width: 200,
      maxWidth: 200,
      cellClass: ['inline-flex', 'items-center', 'justify-end'],
      cellRenderer: CellCaseTrackingGuestActionsComponent,
      cellRendererParams: {
        findingsCallback: (item: CaseIncidentTableEntry) => {
          this.findings.set(item.summary);
          this.findingsModal().show();
        },
      },
    },
  ];

  public readonly tableDef: GridOptions<CaseIncidentTableEntry> = {
    ...defaultTableDefOptions(),
    rowHeight: 56,
    groupDisplayType: 'groupRows',
    groupRowRenderer: 'agGroupCellRenderer',
    pagination: true,
    paginationPageSize: 10,
    paginationPageSizeSelector: [10, 20, 50],
    onRowSelected: (event: RowSelectedEvent<CaseIncidentTableEntry>) => {
      const children = event.node.allLeafChildren;
      const currentSelection = this.selectedCases();
      const newSelection = currentSelection.slice();
      children.forEach((child) => {
        if (currentSelection.includes(child.data)) pull(newSelection, child.data);
        else newSelection.push(child.data);
      });

      this.selectedCases.set(newSelection);
    },
    rowSelection: {
      mode: 'multiRow',
      checkboxLocation: 'autoGroupColumn',
      groupSelects: 'self',
      isRowSelectable: (rowNode) => this.selectedLocationIds().length === 1 && rowNode.group,
      hideDisabledCheckboxes: true,
    },
    selectionColumnDef: {
      maxWidth: 55,
      headerClass: 'pl-[43px]',
      suppressHeaderMenuButton: true,
    },
    groupRowRendererParams: {
      suppressCount: true,
      innerRenderer: CellCaseTrackingGuestGroupComponent,
    },
  };

  ngOnInit() {
    this.incidentDateFrom.set(DateTime.now().minus({ days: 30 }).toJSDate());
    this.incidentDateTo.set(new Date());

    this.loadingLocations.set(true);
    this.locationService
      .getAllLocations({ count_incidents: true, has_plan: true, vertical: 'MicroMarket' })
      .pipe(finalize(() => this.loadingLocations.set(false)))
      .subscribe({
        next: (locations) => {
          const map = new Map<number, LocationResponseWithGroup[]>();
          const allLocations: LocationResponseWithGroup[] = locations.items;
          allLocations.forEach((location) => {
            const branchId = location.branch_id;
            if (!map.has(branchId)) {
              map.set(branchId, []);
            }
            map.get(branchId)?.push(location);
          });
          this.branchesAndLocationsMap.set(map);

          allLocations.forEach((loc) => (loc._group = loc.has_recent_incidents ? 'Has Recent Incidents' : 'Other Locations'));
          this.allLocations.set(sortBy(allLocations, (l) => l.name.trim()));
          this.selectDefaultLocationOnInit();
          setTimeout(() => {
            this.loadFromURLParams();
          }, 0);
        },
        error: (err) => this.error.set(tryGetErrorMessage(err)),
      });
  }

  private loadFromURLParams() {
    const locationIds = this.pageLocationIds();

    if (locationIds.length > 0) {
      this.selectedLocationIds.set(locationIds);
      this.refresh();
    }
  }

  locationGroupBy(loc: CaseTrackingLocation) {
    return loc._group;
  }

  selectDefaultLocationOnInit() {
    const locations = this.allLocations();
    if (locations.length === 0) return;

    const preSelectedIds = new Set(this.pageLocationIds());
    const locationsPreSelected = locations.map((l) => l.id).filter((id) => preSelectedIds.has(id));
    this.selectedLocationIds.set(locationsPreSelected.length ? locationsPreSelected : [locations[0].id]);
    this.onSelection();
  }

  onSelection() {
    this.selectedCases.set([]);
    this.pageLocationIds.set(this.selectedLocationIds());
    this.error.set(undefined);
    this.refresh();
  }

  refresh() {
    if (!this.canRefresh()) {
      return;
    }

    this.loadingCases.set(true);
    this.caseTrackingService
      .getCasesByLocations({
        location_ids: this.selectedLocationIds(),
        branch_ids: [],
        location_group_ids: [],
        incident_date_from: this.incidentDateFrom(),
        incident_date_to: this.incidentDateTo(),
        mode: 'guest',
      })
      .pipe(finalize(() => this.loadingCases.set(false)))
      .subscribe(
        (response: Pagination<CaseWithIncidents>) => {
          this.cases.set(response.items ?? []);
        },
        (err) => this.error.set(tryGetErrorMessage(err)),
      );
  }

  ensureMinIncidents() {
    this.minIncidents.set(Math.max(0, this.minIncidents()));
  }

  expandAll() {
    this.caseGrid()?.api?.expandAll();
  }

  collapseAll() {
    this.caseGrid()?.api?.collapseAll();
  }

  share(shareData: Partial<ShareCasesRequest>) {
    this.isSharing.set(true);
    this.caseTrackingService
      .shareCases({
        ...shareData,
        case_ids: this.selectedCases().map((c) => c.id),
      })
      .pipe(
        finalize(() => {
          this.isSharing.set(false);
          this.selectedCases.set([]);
        }),
      )
      .subscribe(
        () => this.notifyService.notify('Successfully shared case report'),
        () => this.notifyService.notify('Failed to share report'),
      );
  }
}
